import { NavigationContextType } from '../lib/NavigationContext';

import authService, { setAuthErrorCallback } from './authService';
import { shouldSuppressRedirect } from '../utils/navigationRedirectHelpers';
import onboardingService from './onboardingService';
import { organizationService } from './organizationService';
import {
  fetchOrganizationForBillingGate,
  isTransientBillingEntitlementFailure,
  probeActiveSubscription,
  pricingPathForOrganization,
  recordBillingEntitlementMetric,
} from './billingEntitlementPolicy';
import { navigationLogger } from '../utils/loggerInstance';
import { handleAuthError } from './authErrorHelper';
import { LOCAL_STORAGE_KEYS, isSafeRelativeRedirect } from '../constants/localStorageKeys';
import { withRetry } from '../utils/apiRetry';

const logger = navigationLogger;

/**
 * A navigation-aware wrapper around authentication functions
 * This service can be used directly without the useNavigation hook
 * when navigation is needed outside of React components
 */
class NavigationService {
  private _navigation: NavigationContextType | null = null;
  private _isRedirecting: boolean = false; // Flag to prevent multiple redirects
  private _lastRedirectTime: number = 0; // Track when the last redirect happened
  private _cachedOnboardingStatus: boolean | null = null; // Cache onboarding status from parallel auth flow

  /**
   * Initialize the navigation service with the navigation context
   */
  init(navigation: NavigationContextType): void {
    this._navigation = navigation;
    logger.info('NavigationService initialized with navigation context');

    // Register the auth error callback to handle redirects from authService
    setAuthErrorCallback((currentPath: string) => {
      // Don't redirect to login from callback routes - let them handle auth themselves
      if (
        currentPath.startsWith('/auth/okta/callback') ||
        currentPath.startsWith('/auth/microsoft/callback')
      ) {
        logger.debug('Skipping auth error redirect for SSO callback route', { currentPath });
        return;
      }
      this.redirectToLogin(currentPath);
    });
  }

  /**
   * Set onboarding data from parallel auth flow to avoid duplicate API calls
   */
  setOnboardingData(hasCompletedOnboarding: boolean) {
    this._cachedOnboardingStatus = hasCompletedOnboarding;
    logger.info('Cached onboarding status from parallel auth flow', { hasCompletedOnboarding });
  }

  /**
   * Clear cached onboarding data
   */
  clearOnboardingCache() {
    this._cachedOnboardingStatus = null;
  }

  private shouldSkipSessionExpirationCheck(currentPath: string): boolean {
    const publicPaths = ['/login', '/signup', '/forgot-password', '/'];
    return publicPaths.includes(currentPath) || shouldSuppressRedirect(currentPath);
  }

  private async validateSessionViaApi(currentPath: string): Promise<void> {
    try {
      const userInfo = await authService.getCurrentUser();
      if (!userInfo.user) {
        logger.info('No user info from API, redirecting to login');
        this.redirectToLogin(currentPath);
      }
    } catch (error: unknown) {
      const isHandled = handleAuthError({
        error,
        context: 'session validation',
        onUnauthorized: () => {
          logger.info('User not authenticated (401), redirecting to login');
          this.redirectToLogin(currentPath);
        },
        shouldSuppressRedirect: true,
      });
      if (!isHandled) logger.error('Error during API validation', error);
    }
  }

  /**
   * Check for session expiration and redirect to login if needed
   * With cookie-based auth, validates via API instead of relying solely on localStorage
   */
  async checkSessionExpiration() {
    const currentPath = window.location.pathname;
    if (this.shouldSkipSessionExpirationCheck(currentPath)) return;

    try {
      await this.validateSessionViaApi(currentPath);
    } catch (error) {
      logger.error('Error checking session expiration', error);
    }
  }

  /**
   * Get the current navigation context
   */
  get navigation(): NavigationContextType {
    if (!this._navigation) {
      throw new Error(
        'Navigation context not set. Make sure NavigationProvider is properly initialized.',
      );
    }
    return this._navigation;
  }

  /**
   * Check if redirect should be throttled
   * Returns true if redirect should be skipped
   */
  private shouldThrottleRedirect(): boolean {
    const now = Date.now();
    if (this._isRedirecting || now - this._lastRedirectTime < 2000) {
      logger.debug('Redirect already in progress or too soon after last redirect, skipping');
      return true;
    }
    return false;
  }

  /**
   * Handle stored redirect path if available
   * Returns true if redirect was handled
   */
  private handleStoredRedirectPath(): boolean {
    const redirectPath = localStorage.getItem(LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
    logger.debug('Checking for stored redirect path', { redirectPath });

    if (
      redirectPath &&
      isSafeRelativeRedirect(redirectPath) &&
      !redirectPath.startsWith('/login') &&
      !redirectPath.startsWith('/signup') &&
      !redirectPath.startsWith('/auth/')
    ) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
      logger.info('Using stored redirect path', { redirectPath });
      this.navigation.navigateTo(redirectPath);
      return true;
    }
    return false;
  }

  /**
   * Get user ID from authenticated user
   * Returns userId or null if not available
   */
  private async getUserIdWithFallback(): Promise<string | null> {
    try {
      const userInfo = await authService.getCurrentUser();
      return userInfo.user?.id || null;
    } catch (error) {
      // For recently authenticated users, we might get API errors initially
      // Check if we have user/session data in the authService directly
      const hasUser = !!authService.user;
      const hasSession = !!authService.session;

      if (hasUser && hasSession) {
        return authService.user?.id || null;
      }
      return null;
    }
  }

  /**
   * Get onboarding status using cache or API
   * Returns onboarding completion status
   */
  private async getOnboardingStatus(userId: string): Promise<boolean> {
    // Use cached onboarding status from parallel auth flow if available
    if (this._cachedOnboardingStatus !== null) {
      const cachedStatus = this._cachedOnboardingStatus;
      logger.debug('Using cached onboarding status', { cachedStatus });
      // Clear cache after use to prevent stale data
      this._cachedOnboardingStatus = null;
      return cachedStatus;
    }

    // Fallback to API call if no cached data
    logger.debug('Making API call to check onboarding status for user', { userId });
    try {
      const status = await onboardingService.hasUserCompletedOnboarding(userId);
      logger.debug('Fetched onboarding completion status', { status });
      return status;
    } catch (onboardingError) {
      logger.error('Failed to check onboarding status', {
        error: onboardingError instanceof Error ? onboardingError.message : String(onboardingError),
      });
      throw onboardingError;
    }
  }

  /**
   * Redirects user to the appropriate location after login based on:
   * 1. Stored redirect path (if any)
   * 2. Onboarding status (completed -> chat, not completed -> user-onboarding)
   * This should ONLY be called after successful login/register, not on page loads
   */
  async redirectAfterLogin() {
    // CRITICAL: Don't redirect if signup is in progress
    if (shouldSuppressRedirect()) {
      return;
    }

    // Prevent multiple redirects within 2 seconds
    if (this.shouldThrottleRedirect()) {
      return;
    }

    this._isRedirecting = true;
    this._lastRedirectTime = Date.now();

    try {
      logger.debug('Starting redirectAfterLogin', { action: 'redirectAfterLogin' });

      // Don't redirect if user is already on email verification page
      const currentPath = window.location.pathname;
      if (shouldSuppressRedirect(currentPath)) {
        logger.debug('User is on email verification page, skipping redirect');
        this._isRedirecting = false;
        return;
      }

      // Keep the signup flag for telemetry/flow tracking, but don't force
      // onboarding before billing checks.
      const isNewSignup = sessionStorage.getItem('new_user_signup') === 'true';
      if (isNewSignup) {
        logger.info('New user signup detected, applying billing-first redirect flow');
      }

      // Check if we have a stored redirect path
      if (this.handleStoredRedirectPath()) {
        this._isRedirecting = false;
        return;
      }

      // No valid redirect path, check authentication status
      if (!authService.isAuthenticated()) {
        logger.info('User not authenticated, redirecting to login');
        this.navigation.navigateTo('/login');
        this._isRedirecting = false;
        return;
      }

      // User is authenticated, get current user info
      const userId = await this.getUserIdWithFallback();
      if (!userId) {
        logger.error('No user ID found in user info');
        this.navigation.navigateTo('/login');
        this._isRedirecting = false;
        return;
      }

      logger.debug('Checking onboarding status for user ID', { userId });

      try {
        let organization: Awaited<ReturnType<typeof organizationService.getOrganization>>;
        try {
          organization = await fetchOrganizationForBillingGate(() =>
            withRetry(() => organizationService.getOrganization(), {
              maxRetries: 2,
              baseDelay: 500,
              shouldRetry: isTransientBillingEntitlementFailure,
            }),
          );
        } catch (error) {
          recordBillingEntitlementMetric({
            surface: 'post_login_redirect',
            organizationOutcome: 'fetch_exhausted',
            error,
          });
          logger.warn(
            'Post-login: organization lookup failed; redirecting to app shell for recovery UI',
            {
              error: error instanceof Error ? error.message : String(error),
            },
          );
          this.navigation.navigateTo('/chat');
          return;
        }

        const subscription = await probeActiveSubscription(organization);
        recordBillingEntitlementMetric({
          surface: 'post_login_redirect',
          organizationOutcome: 'success',
          organization,
          subscription,
        });

        if (subscription.status === 'fail_closed') {
          const pricingPath = pricingPathForOrganization(organization);
          logger.warn(
            'Post-login: subscription verification failed (fail-closed); redirecting to billing',
            {
              path: pricingPath,
              error:
                subscription.error instanceof Error
                  ? subscription.error.message
                  : String(subscription.error),
            },
          );
          this.navigation.navigateTo(pricingPath);
          return;
        }

        if (subscription.status === 'inactive') {
          const pricingPath = pricingPathForOrganization(organization);
          logger.info('User has no subscription, redirecting to pricing', { path: pricingPath });
          this.navigation.navigateTo(pricingPath);
          return;
        }

        if (subscription.status === 'transient') {
          logger.warn(
            'Post-login: subscription check inconclusive (transient); continuing redirect flow',
            {
              error:
                subscription.error instanceof Error
                  ? subscription.error.message
                  : String(subscription.error),
            },
          );
        }

        if (organization && !organization.intake_completed_at) {
          if (organization.role === 'owner' || organization.role === 'admin') {
            this.navigation.navigateTo('/onboarding');
          } else {
            this.navigation.navigateTo('/org-setup-pending');
          }
          return;
        }

        const hasCompletedOnboarding = await this.getOnboardingStatus(userId);

        // If we're already on chat (main app), don't redirect again
        if (currentPath === '/chat' && hasCompletedOnboarding) {
          logger.debug('Already on chat, no redirect needed');
          this._isRedirecting = false;
          return;
        }

        if (hasCompletedOnboarding) {
          logger.info('User has completed onboarding and subscription, redirecting to chat');
          this.navigation.navigateTo('/chat');
        } else {
          const isB2BOrgUser = !!organization && !!organization.intake_completed_at;
          logger.info('User has not completed onboarding, redirecting to onboarding entry step', {
            isB2BOrgUser,
          });
          this.navigation.navigateTo('/user-onboarding');
        }
      } catch (error) {
        logger.error('Error checking onboarding status', error, { action: 'redirectAfterLogin' });
        this.navigation.navigateTo('/login');
      }
    } catch (error) {
      logger.error('Error during redirect after login', error, { action: 'redirectAfterLogin' });
    } finally {
      this._isRedirecting = false;
    }
  }

  /**
   * Check if user has completed onboarding
   * This is a separate method that can be called from other components
   */
  async checkAndRedirectIfNeeded() {
    try {
      if (!authService.isAuthenticated()) {
        return false;
      }

      const userInfo = await authService.getCurrentUser();
      const userId = userInfo.user?.id;

      if (!userId) {
        return false;
      }

      const onboardingStatus = await onboardingService.hasUserCompletedOnboarding(userId);

      if (!onboardingStatus) {
        this.navigation.navigateTo('/user-onboarding');
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error checking onboarding status', {
        error,
        action: 'checkAndRedirectIfNeeded',
      } as const);
      return false;
    }
  }

  /**
   * @deprecated Use checkAndRedirectIfNeeded instead
   */
  async checkOnboardingStatus(_userId: string) {
    logger.warn('checkOnboardingStatus is deprecated, use checkAndRedirectIfNeeded instead', {
      action: 'deprecationWarning',
    } as const);
    // This method is deprecated and no longer performs any operations
    return null;
  }

  async handleOnboardingRedirect(userId: string) {
    try {
      const hasCompleted = await onboardingService.hasUserCompletedOnboarding(userId);

      if (hasCompleted) {
        logger.info('User has completed onboarding, going to chat');
        this.navigation.navigateToChat();
      } else {
        logger.info('User needs to complete onboarding');
        this.navigation.navigateTo('/user-onboarding');
      }
    } catch (error) {
      logger.error('Error checking onboarding status', error);
    }
  }

  /**
   * Redirects user to login page and stores current path for redirect after login
   */
  redirectToLogin(currentPath?: string) {
    // Don't redirect from email verification page or during signup
    const path = currentPath || window.location.pathname;
    if (shouldSuppressRedirect(path)) {
      return;
    }

    if (currentPath) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN, currentPath);
    }
    this.navigation.navigateToLogin();
  }

  /**
   * Logs out the user and redirects to login page
   */
  async logout() {
    await authService.logout();
    this.navigation.navigateToLogin();
  }
}

export const navigationService = new NavigationService();
export default navigationService;
